/**
 * Whether the Microsoft sign-in button should be shown.
 *
 * The button is gated on runtime config rather than shipped visible, and the
 * reason is that a visible button which cannot work is worse than no button.
 * `signInWithPopup` against a provider Identity Platform has not been given
 * fails with `auth/operation-not-allowed` — a popup that opens, flashes, and
 * dies, with nothing on screen explaining why. Microsoft was removed from this
 * product once already for exactly that reason ("no Azure tenant is
 * configured"), and it should not come back in the same state.
 *
 * So the code can land before the provider is enabled, and the button appears
 * the moment `MICROSOFT_SSO` is set — no deploy of application code required,
 * just the config the entrypoint already writes.
 *
 * Reads the same `window.__APP__` block the allowed-hosts value uses — the
 * type lives with that one in shared/config/env.ts, not here — with a
 * build-time env fallback, mirroring `googleClientId()` in the calendar
 * connector so there is one shape for "is this integration configured".
 */

/**
 * True only for an explicit opt-in.
 *
 * Anything else — unset, empty, the literal string the entrypoint writes when
 * the variable is absent — reads as off. A truthiness check would enable the
 * button on the string "false", which is exactly what an unset shell variable
 * interpolated into config.js can produce.
 */
export function microsoftSsoEnabled(): boolean {
  const runtime = window.__APP__?.MICROSOFT_SSO;
  const build = import.meta.env.VITE_MICROSOFT_SSO as string | undefined;
  const value = (runtime || build || '').trim().toLowerCase();
  return value === 'true' || value === '1';
}
